import React, {
  EventHandler, memo, MouseEvent, useCallback, useEffect,
} from 'react';
import { useStore } from 'react-redux';

import { useFlipbookContext } from 'app/components/common/flipbook/lib/hooks';
import Envelope from 'app/components/common/Envelope';

import MediaPlayer from 'app/lib/MediaPlayer';
import Video from 'app/lib/Video';
import Audio from 'app/lib/Audio';
import AdaptrFeedFm from 'lib/AdaptrFeedFm';
import Resource from 'app/lib/Resource';
import { useMediaResource } from 'app/lib/MediaHooks';
import { useSelector, useDispatcher } from 'app/store/utils/redux/hooks';
import { IPageCover } from 'app/store/nodes/card/interfaces';

interface ICover {
  data?: IPageCover
  className?: string,
  handleTapToOpen?: EventHandler<MouseEvent>,
}

const Cover: React.FC<ICover> = memo((props) => {
  const {
    data,
    className,
  } = props;

  const actions = useDispatcher();
  const store = useStore();
  const { controller } = useFlipbookContext();

  const cardState = useSelector((state) => state?.card?.meta?.state);
  const cardStatus = useSelector((state) => state?.card?.meta?.status || 'init');
  const senderName = useSelector((state) => state?.card?.common?.sender || '');

  const sender = data?.items?.sender || senderName;
  const theme = data?.items?.theme;
  const { containerRef: stampContainerRef } = useMediaResource(data?.items?.stamp);
  const { containerRef: logoContainerRef } = useMediaResource(data?.items?.logo);
  const { containerRef: backgroundContainerRef } = useMediaResource(data?.items?.background);
  useEffect(() => {
    if (cardStatus !== 'ready') {
      return;
    }
    actions.hint.hide();
  }, [actions.hint, cardStatus]);

  const handleTapToOpen = useCallback(() => {
    const appAudioId = store.getState()?.card?.common?.audio?.id;
    const resources = store.getState()?.card?.resources;
    if (cardState === 'opened' && controller) {
      controller.flipLeft();
      return;
    }
    setTimeout(() => {
      const audio = Resource.get(appAudioId);
      if (appAudioId && audio?.object) {
        (audio.object as Audio | AdaptrFeedFm).loop(true);
        MediaPlayer.play(appAudioId);
      }
      actions.card.sendOpened();
      actions.card.setState('opened');
      if (controller) {
        controller.updateConfig({
          enabledFlip: true,
        }).flipLeft();
      }
    }, 50);
    Promise
      .all(resources.filter((item) => item?.type === 'video').map((resource: any) => {
        const video = Resource.get(resource?.id);
        return (video?.object as Video).playAsync();
      }))
      .then((results: Video[]) => {
        results.forEach((video) => {
          video.pause();
        });
      });
  }, [controller, cardState]);

  const colors = [theme?.color1 || '', theme?.color2 || '', theme?.color2 || ''];
  return (
    <Envelope
      cardState={cardState}
      cardStatus={cardStatus}
      senderName={sender}
      stampContainerRef={stampContainerRef}
      logoContainerRef={data?.items?.logo ? logoContainerRef : undefined}
      backgroundContainerRef={data?.items?.background ? backgroundContainerRef : undefined}
      className={className}
      handleTapToOpen={handleTapToOpen}
      colors={colors}
      isCustom={Boolean(colors[0])}
    />
  );
});

export default Cover;
